import {
  Component, EventEmitter, Input, Output, OnInit,
  ChangeDetectionStrategy, ChangeDetectorRef, HostListener
} from '@angular/core';
import { Tutorial, TutorialStep } from '../../models';
import { TutorialService } from '../../services/tutorial.service';

/**
 * Step Viewer Component
 * Full-screen overlay that displays tutorial steps one at a time
 * with prev/next navigation and a step progress indicator.
 *
 * Uses a clean Input → local state pattern:
 *  - `startAtStep` (Input): the initial step index (seed value, read once)
 *  - `currentStepIndex` (local): mutable working state owned by this component
 *  - `stepChange` (Output): emits on every navigation so the parent stays in sync
 */
@Component({
  selector: 'app-step-viewer',
  templateUrl: './step-viewer.component.html',
  styleUrls: ['./step-viewer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class StepViewerComponent implements OnInit {
  /** The tutorial whose steps are displayed */
  @Input() tutorial: Tutorial | null = null;

  /** Seed value — which step to start on (read once in ngOnInit) */
  @Input() startAtStep = 0;

  /** Emits the current step index when the viewer is closed (back to sidebar) */
  @Output() closeViewer = new EventEmitter<number>();

  /** Emits the current step index and signals "close everything" */
  @Output() dismissAll = new EventEmitter<number>();

  /** Emits on every step navigation so the parent/sidebar can stay in sync */
  @Output() stepChange = new EventEmitter<number>();

  /** Emits to signal "go back to tutorials list" */
  @Output() backToList = new EventEmitter<void>();

  /** Local mutable state — the active step index */
  currentStepIndex = 0;

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly tutorialService: TutorialService
  ) {}

  /** Initialize the working step index from the seed input */
  ngOnInit(): void {
    this.currentStepIndex = this.startAtStep;
  }

  /** Returns the full image URL for a step using the correct API endpoint */
  getStepImageUrl(step: TutorialStep): string {
    return this.tutorialService.getStepImageUrl(step);
  }

  /** Returns the current step based on index */
  get currentStep(): TutorialStep | null {
    if (!this.tutorial?.Steps?.length) { return null; }
    return this.tutorial.Steps[this.currentStepIndex] || null;
  }

  /** Total number of steps */
  get totalSteps(): number {
    return this.tutorial?.Steps?.length || 0;
  }

  /** Whether we can go to the previous step */
  get hasPrev(): boolean {
    return this.currentStepIndex > 0;
  }

  /** Whether we can go to the next step */
  get hasNext(): boolean {
    return this.currentStepIndex < this.totalSteps - 1;
  }

  /** Progress percentage for the progress bar */
  get progressPercent(): number {
    if (this.totalSteps <= 1) { return 100; }
    return ((this.currentStepIndex + 1) / this.totalSteps) * 100;
  }

  /** Navigate to the previous step */
  prevStep(): void {
    if (this.hasPrev) {
      this.currentStepIndex--;
      this.stepChange.emit(this.currentStepIndex);
      this.cdr.markForCheck();
    }
  }

  /** Navigate to the next step */
  nextStep(): void {
    if (this.hasNext) {
      this.currentStepIndex++;
      this.stepChange.emit(this.currentStepIndex);
      this.cdr.markForCheck();
    }
  }

  /** Jump to a specific step */
  goToStep(index: number): void {
    if (index >= 0 && index < this.totalSteps) {
      this.currentStepIndex = index;
      this.stepChange.emit(this.currentStepIndex);
      this.cdr.markForCheck();
    }
  }

  /** TrackBy function for step dots */
  trackByStepIndex(index: number): number {
    return index;
  }

  /** Close the viewer and emit the current step index for continuity */
  onClose(): void {
    this.closeViewer.emit(this.currentStepIndex);
  }

  /** Dismiss everything — clicked outside image/navigators */
  onDismissAll(): void {
    this.dismissAll.emit(this.currentStepIndex);
  }

  /** Dismiss viewer and go back to tutorials list */
  onBackToList(): void {
    this.backToList.emit();
  }

  /** Keyboard navigation: arrow keys for prev/next, Escape to close */
  @HostListener('document:keydown', ['$event'])
  handleKeyboard(event: KeyboardEvent): void {
    if (!this.tutorial) { return; }

    switch (event.key) {
      case 'ArrowLeft':
        this.prevStep();
        break;
      case 'ArrowRight':
        this.nextStep();
        break;
      case 'Escape':
        this.onClose();
        break;
    }
  }
}
